/**
 * "5. Chỉnh sửa nâng cao": the desktop "AI chỉnh sửa trực tiếp" choices (catalog.json "ai"). Plain words only: no
 * engine or quality settings on the page; the render machine picks them. Plus / Gold members also choose a "Model"
 * (premium models through a provider API, paid from top-up tokens only): only names, one sentence and prices show,
 * with a consent box per provider (the photo leaves DTAA's servers), remembered in this browser.
 */
import { useId, useState, type Ref } from "react";
import type { AiEdit, Catalog, JobSettings, PremiumModel } from "../api";
import { PREMIUM, QUEUE } from "../messages";
import { OutfitPicker, PremiumModelField } from "./AiPickers";

export const EMPTY_AI: AiEdit = {
  frontal: false, expression: "keep", relight: false, lighting: "studio", style: "", outfit: "keep", outfit_text: "",
  tidy_hair: false, remove_glasses: false,
};

/** Same rule as the server (AiEditIn.any_task): something for the AI to do. The expression only counts together with
 * "Xoay mặt nhìn thẳng" (the render machine ignores it otherwise, keeping the customer's own face). */
export function aiHasTask(a: AiEdit): boolean {
  return a.frontal || a.relight || a.outfit !== "keep" || a.tidy_hair || a.remove_glasses;
}

/** What is sent: the expression of a preset stays in the form, but is sent only with "Xoay mặt nhìn thẳng". */
function sent(a: AiEdit): AiEdit {
  return a.frontal ? a : { ...a, expression: "keep" };
}

/** The premium model chosen in these settings, when this account is offered it. */
export function chosenModel(models: PremiumModel[] | undefined, ai: AiEdit | undefined): PremiumModel | undefined {
  return ai?.model ? models?.find((m) => m.key === ai.model) : undefined;
}

interface Props {
  catalog: Catalog;
  value: JobSettings;
  onChange: (v: JobSettings) => void;
  /** what one edit costs with the current choice (standard or the chosen model's price) */
  cost: number;
  /** the standard edit's price (the "Tiêu chuẩn" option) */
  standardCost?: number;
  /** premium models offered to this account (Plus / Gold); none = no "Model" choice */
  models?: PremiumModel[];
  /** provider key → consent given in this browser */
  consents?: Record<string, boolean>;
  onConsent?: (provider: string, given: boolean) => void;
  consentRef?: Ref<HTMLInputElement>;
  /** v4: no render machine serves the AI queue now (queues.ts): said here, the render button waits */
  paused?: boolean;
}

export function AiFields({ catalog, value, onChange, cost, standardCost = cost, models = [], consents = {}, onConsent,
                          consentRef, paused = false }: Props) {
  const ids = { on: useId(), preset: useId(), text: useId(), frontal: useId(), expression: useId(),
                relight: useId(), lighting: useId(), style: useId(), hair: useId(), glasses: useId() };
  const ai = catalog.ai;
  const [draft, setDraft] = useState<AiEdit>(value.ai ?? EMPTY_AI);   // kept while switched off
  const [group, setGroup] = useState(ai?.groups[0]?.key ?? "nam");
  const [presetKey, setPresetKey] = useState("");
  if (!ai) return <p className="hint">Chức năng này chưa có trên máy chủ.</p>;
  const on = value.ai !== undefined;
  const model = chosenModel(models, draft);

  function commit(next: AiEdit, patch: Partial<JobSettings> = {}) {
    setDraft(next);
    onChange({ ...value, ...patch, ai: sent(next) });
  }
  const set = (patch: Partial<AiEdit>) => commit({ ...draft, ...patch });

  function chooseModel(key: string) {
    const next: AiEdit = { ...draft, model: key };
    if (!key) delete next.model;                          // the standard edit sends no model at all
    commit(next);
  }

  function applyPreset(key: string, forGroup = group) {
    setPresetKey(key);
    const p = ai?.presets.find((x) => x.key === key);
    if (!p) return;
    const background = catalog.backgrounds.some((b) => b.key === p.background) ? { background: p.background, custom_rgb: null } : {};
    commit({ ...draft, expression: p.expression, relight: p.relight, lighting: p.lighting, style: p.style,
             tidy_hair: p.tidy_hair, remove_glasses: p.remove_glasses, outfit: p.outfits[forGroup] ?? draft.outfit,
             outfit_text: "" }, background);
  }

  function chooseGroup(g: string) {
    setGroup(g);
    if (presetKey) applyPreset(presetKey, g);
    else if (draft.outfit !== "keep" && draft.outfit !== "custom" &&
             !(ai?.groups.find((x) => x.key === g)?.outfits ?? []).includes(draft.outfit)) set({ outfit: "keep" });
  }

  return (
    <div className="options ai-fields">
      {paused && <p className="notice notice-ai" role="status"><strong>{QUEUE.aiPaused}.</strong> {QUEUE.aiPausedHint}</p>}
      <div className="check">
        <input id={ids.on} type="checkbox" checked={on}
               onChange={(e) => onChange({ ...value, ai: e.target.checked ? sent(draft) : undefined })} />
        <label htmlFor={ids.on}>Bật chỉnh sửa nâng cao ({cost ? `${cost} token mỗi lần` : "miễn phí"})</label>
      </div>
      {on && (
        <>
          {models.length > 0 && (
            <PremiumModelField models={models} value={model?.key ?? ""} onChange={chooseModel} standardCost={standardCost}
                               consent={Boolean(model && consents[model.provider])} consentRef={consentRef}
                               onConsent={(given) => { if (model) onConsent?.(model.provider, given); }}
                               idleHint="Model cao cấp cho ảnh đẹp hơn; trừ token nạp." />
          )}
          <div className="field">
            <label htmlFor={ids.preset}>Mẫu ảnh thẻ</label>
            <select id={ids.preset} value={presetKey} onChange={(e) => applyPreset(e.target.value)}>
              <option value="">— Không dùng mẫu —</option>
              {ai.presets.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>

          <OutfitPicker groups={ai.groups} group={group} onGroup={chooseGroup} value={draft.outfit}
                        onChange={(k) => set({ outfit: k, outfit_text: k === "custom" ? draft.outfit_text : "" })}
                        labelOf={(k) => ai.outfits[k] ?? k} extra={["custom"]} groupLegend="Đối tượng" outfitLabel="Trang phục" />
          {draft.outfit === "custom" && (
            <div className="field">
              <label htmlFor={ids.text}>Mô tả trang phục</label>
              <input id={ids.text} className="text-input" type="text" maxLength={ai.outfit_text_max} value={draft.outfit_text}
                     placeholder="Ví dụ: áo sơ mi trắng cổ đức, cài kín cúc"
                     onChange={(e) => set({ outfit_text: e.target.value })} />
            </div>
          )}

          <div className="check">
            <input id={ids.frontal} type="checkbox" checked={draft.frontal} onChange={(e) => set({ frontal: e.target.checked })} />
            <label htmlFor={ids.frontal}>Xoay mặt nhìn thẳng</label>
          </div>
          <div className="field-row">
            <div className="field">
              <label htmlFor={ids.expression}>Nét mặt</label>
              <select id={ids.expression} value={draft.frontal ? draft.expression : "keep"} disabled={!draft.frontal}
                      title={draft.frontal ? undefined : "Chỉ đổi được khi bật “Xoay mặt nhìn thẳng”"}
                      onChange={(e) => set({ expression: e.target.value })}>
                {ai.expressions.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
              </select>
            </div>
            <div className="field">
              <label htmlFor={ids.style}>Phong cách</label>
              <select id={ids.style} value={draft.style} onChange={(e) => set({ style: e.target.value })}>
                {ai.styles.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
              </select>
            </div>
          </div>
          <div className="check">
            <input id={ids.relight} type="checkbox" checked={draft.relight} onChange={(e) => set({ relight: e.target.checked })} />
            <label htmlFor={ids.relight}>Ánh sáng studio</label>
          </div>
          {draft.relight && (
            <div className="field">
              <label htmlFor={ids.lighting}>Kiểu sáng</label>
              <select id={ids.lighting} value={draft.lighting} onChange={(e) => set({ lighting: e.target.value })}>
                {ai.lights.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
              </select>
            </div>
          )}
          <div className="check">
            <input id={ids.hair} type="checkbox" checked={draft.tidy_hair} onChange={(e) => set({ tidy_hair: e.target.checked })} />
            <label htmlFor={ids.hair}>Chải gọn tóc</label>
          </div>
          <div className="check">
            <input id={ids.glasses} type="checkbox" checked={draft.remove_glasses}
                   onChange={(e) => set({ remove_glasses: e.target.checked })} />
            <label htmlFor={ids.glasses}>Bỏ kính</label>
          </div>
          <p className="hint">{model ? PREMIUM.time : "Mỗi lần mất khoảng 1–2 phút."}</p>
        </>
      )}
    </div>
  );
}
